class LightboxModal {
    constructor() {
        if (window.imageThemeLightboxModal) {
            return window.imageThemeLightboxModal;
        }

        window.imageThemeLightboxModal = this;

        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => this.initModal());
            return;
        }

        this.initModal();
    }

    initModal() {
        if (this.isInitialized) {
            return;
        }

        this.galleries = Array.from(document.querySelectorAll(".wp-block-gallery.lightbox-modal"))
            .map((gallery) => ({
                element: gallery,
                items: Array.from(gallery.querySelectorAll(".wp-block-image img")),
            }))
            .filter((gallery) => gallery.items.length > 0);

        if (!this.galleries.length) {
            return;
        }

        this.upgradeGalleryImagesToOriginal();
        this.currentGalleryIndex = -1;
        this.currentIndex = 0;
        this.zoomLevel = 1;
        this.lightboxModal = this.createLightbox();
        this.bindEvents();
        this.isInitialized = true;
    }

    upgradeGalleryImagesToOriginal() {
        this.galleries.forEach((gallery) => {
            gallery.items.forEach((image) => {
                const originalSrc = image.getAttribute("src");
                if (!originalSrc) {
                    return;
                }

                image.src = originalSrc;
                image.removeAttribute("srcset");
                image.removeAttribute("sizes");
            });
        });
    }

    createLightbox() {
        document
            .querySelectorAll(".lightbox-modal-carousel")
            .forEach((existingLightbox) => existingLightbox.remove());

        const lightbox = document.createElement("div");
        lightbox.className = "lightbox-modal-carousel";
        lightbox.setAttribute("aria-hidden", "true");
        lightbox.setAttribute("tabindex", "-1");
        lightbox.innerHTML = `
            <button type="button" class="lightbox-modal-carousel__close" aria-label="Close">&times;</button>
            <button type="button" class="lightbox-modal-carousel__prev" aria-label="Previous image">&#10094;</button>
            <button type="button" class="lightbox-modal-carousel__next" aria-label="Next image">&#10095;</button>
            <span class="lightbox-modal-carousel__counter">1/1</span>
            <div class="lightbox-modal-carousel__content">
                <img class="lightbox-modal-carousel__image" src="" alt="" />
                <p class="lightbox-modal-carousel__caption" hidden></p>
            </div>
        `;

        document.body.appendChild(lightbox);
        this.lightboxImage = lightbox.querySelector(".lightbox-modal-carousel__image");
        this.imageCounter = lightbox.querySelector(".lightbox-modal-carousel__counter");
        this.lightboxCaption = lightbox.querySelector(".lightbox-modal-carousel__caption");
        return lightbox;
    }

    bindEvents() {
        this.galleries.forEach((gallery, galleryIndex) => {
            gallery.items.forEach((item, imageIndex) => {
                item.addEventListener("click", (event) => {
                    event.preventDefault();
                    this.openModal(galleryIndex, imageIndex);
                });
            });
        });

        this.lightboxModal
            .querySelector(".lightbox-modal-carousel__close")
            .addEventListener("click", () => this.closeModal());
        this.lightboxModal
            .querySelector(".lightbox-modal-carousel__prev")
            .addEventListener("click", () => this.changeSlide(-1));
        this.lightboxModal
            .querySelector(".lightbox-modal-carousel__next")
            .addEventListener("click", () => this.changeSlide(1));

        this.lightboxImage.addEventListener("click", (event) => {
            event.stopPropagation();
            this.toggleZoom();
        });

        this.lightboxImage.addEventListener("mousemove", (event) => {
            if (this.zoomLevel > 1) {
                this.adjustImagePosition(event);
            }
        });

        this.lightboxModal.addEventListener("click", (event) => {
            if (event.target === this.lightboxModal) {
                this.closeModal();
            }
        });

        document.addEventListener("keydown", (event) => {
            if (!this.lightboxModal.classList.contains("is-open")) {
                return;
            }

            if (event.key === "Escape") {
                this.closeModal();
            } else if (event.key === "ArrowLeft") {
                this.changeSlide(-1);
            } else if (event.key === "ArrowRight") {
                this.changeSlide(1);
            }
        });
    }

    getCurrentGallery() {
        return this.galleries[this.currentGalleryIndex];
    }

    getImageSource(imageElement) {
        const parentLink = imageElement.closest("a");
        if (parentLink?.href) {
            return parentLink.href;
        }

        return imageElement.getAttribute("src") || imageElement.currentSrc || "";
    }

    getImageCaption(imageElement) {
        const figure = imageElement.closest(".wp-block-image");
        if (!figure) {
            return "";
        }

        const caption = figure.querySelector("figcaption");
        return caption ? caption.textContent.trim() : "";
    }

    openModal(galleryIndex, imageIndex) {
        this.currentGalleryIndex = galleryIndex;
        this.currentIndex = imageIndex;
        this.updateSlide();

        this.lightboxModal.classList.add("is-open");
        this.lightboxModal.setAttribute("aria-hidden", "false");
        requestAnimationFrame(() => {
            this.lightboxModal.focus({ preventScroll: true });
        });
    }

    closeModal() {
        this.lightboxModal.classList.remove("is-open");
        this.lightboxModal.setAttribute("aria-hidden", "true");
        this.resetZoom();
    }

    updateSlide() {
        const gallery = this.getCurrentGallery();
        if (!gallery) {
            return;
        }

        const image = gallery.items[this.currentIndex];
        if (!image) {
            return;
        }

        this.lightboxImage.src = this.getImageSource(image);
        this.lightboxImage.alt = image.getAttribute("alt") || "";
        const captionText = this.getImageCaption(image);
        if (captionText) {
            this.lightboxCaption.textContent = captionText;
            this.lightboxCaption.hidden = false;
            this.lightboxModal.classList.add("has-caption");
        } else {
            this.lightboxCaption.textContent = "";
            this.lightboxCaption.hidden = true;
            this.lightboxModal.classList.remove("has-caption");
        }
        this.imageCounter.textContent = `${this.currentIndex + 1}/${gallery.items.length}`;
        this.resetZoom();
    }

    changeSlide(direction) {
        const gallery = this.getCurrentGallery();
        if (!gallery) {
            return;
        }

        this.currentIndex += direction;
        if (this.currentIndex >= gallery.items.length) {
            this.currentIndex = 0;
        } else if (this.currentIndex < 0) {
            this.currentIndex = gallery.items.length - 1;
        }

        this.updateSlide();
    }

    toggleZoom() {
        this.zoomLevel = this.zoomLevel === 1 ? 2 : 1;
        this.lightboxImage.style.transform = `scale(${this.zoomLevel})`;
        this.lightboxImage.style.transformOrigin = "0 0";
    }

    adjustImagePosition(event) {
        const rect = this.lightboxImage.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;
        this.lightboxImage.style.transformOrigin = `${xPercent}% ${yPercent}%`;
    }

    resetZoom() {
        this.zoomLevel = 1;
        this.lightboxImage.style.transform = "scale(1)";
        this.lightboxImage.style.transformOrigin = "0 0";
    }

}

export default LightboxModal;